import { Moment } from "moment"
import IInvoice from "../interfaces/IInvoice"
import { get, patch, post, transformRequestData } from "./utils/requests"
import InvoiceDecorator from "../decorators/InvoiceDecorator"
import { LineItemParams, OrderParams } from "./orders"
import { DeepCamelCaseKeys } from "~/types/utilities"
import { INVOICE_FILTER_OPTIONS } from "~/components/Admin/Invoices/InvoiceFilterOptions"

export type tSortableColumns = "invoice_date" | "due_date" | "name" | "kind"

const fetchForDates = async (
  campId: string,
  data: {
    from: Moment
    until: Moment
    filter: string
    statusFilter: INVOICE_FILTER_OPTIONS
    sortBy?: tSortableColumns
    sortOrder?: "asc" | "desc"
    includeArchived?: boolean
  },
  page: number,
): Promise<{ invoices: InvoiceDecorator[]; total_count: number }> => {
  const query = transformRequestData(data)
  query.page = page

  return get(`/api/camps/${campId}/invoices`, query).then(
    (response: { invoices: IInvoice[]; meta: { total_count: number } }) => {
      const { invoices, meta } = response

      return {
        invoices: invoices.map((inv) => new InvoiceDecorator(inv)),
        total_count: meta.total_count,
      }
    },
  )
}

const fetchForOrder = async (orderId: string): Promise<{ invoice: IInvoice }> => get(`/api/orders/${orderId}/invoice`)

const find = async (campId: string, id: string): Promise<{ invoice: IInvoice }> =>
  get(`/api/camps/${campId}/invoices/${id}`)

const createForReservation = async (
  reservationId: string,
  isCreditCardFeeEnabled: boolean = false,
  isTaxFeeEnabled: boolean = false,
): Promise<{ invoice: IInvoice }> => {
  const payload: any = {
    is_credit_card_fee_enabled: isCreditCardFeeEnabled,
    is_tax_fee_enabled: isTaxFeeEnabled,
  };

  const response = await post(`/api/reservations/${reservationId}/invoices`, payload)

  const { invoice }: { invoice: IInvoice } = response
  return { invoice: new InvoiceDecorator(invoice) }
};

interface UpdateData {
  isSending: boolean
  sendingMode: string
  admin_email?: string
  sendDate: Moment
  dueDate: Moment
  recipientEmail: string
  recipientPhone: string
  recipientName: string
  recipientAddress: string
  memo: string
  lastMeterReading: number
  meterReading: number
  isCreditCardFeeEnabled?: boolean
  lineItemsAttributes: DeepCamelCaseKeys<LineItemParams>[]
}

const update = async (id: string, campId: string, data: UpdateData): Promise<{ invoice: IInvoice }> =>
  patch(`/api/camps/${campId}/invoices/${id}`, transformRequestData(data)).then(({ invoice }: { invoice: IInvoice }) =>
    Promise.resolve({ invoice: new InvoiceDecorator(invoice) }),
  )

const updateMany = async (
  campId: string,
  data: {
    invoices: {
      invoiceId: string
      sendingMode: string
      isSending: boolean
    }[]
  },
): Promise<{ invoices: IInvoice[]; records?: (IInvoice & { errors: string[] })[] }> =>
  patch(`/api/camps/${campId}/invoices/batch`, transformRequestData(data)).then(
    ({ invoices }: { invoices: IInvoice[] }) =>
      Promise.resolve({ invoices: invoices.map((inv) => new InvoiceDecorator(inv)) }),
  )

const Invoices = {
  createForReservation,
  fetchForDates,
  fetchForOrder,
  find,
  update,
  updateMany,
}

export default Invoices
